import { useEffect, useMemo, useState } from 'react';
import Editor, { useMonaco } from '@monaco-editor/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { useAuth } from '../contexts/AuthContext';
import { syncAdminAPI } from '../utils/api';
import { SyncDaemonNode, SyncDispatchJob, SyncTenantAccess } from '../types';

type ExplorerMode = 'query_preview' | 'list_tables' | 'list_columns';

type DispatchOverrides = {
  mode?: ExplorerMode;
  page?: number;
  pageSize?: number;
  schemaName?: string;
  tableName?: string;
  sqlText?: string;
  params?: Record<string, any>;
};

function formatDate(value?: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('it-IT');
}

export default function SyncQueryExplorer() {
  const { user } = useAuth();
  const monaco = useMonaco();

  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [mode, setMode] = useState<ExplorerMode>('query_preview');
  const [schemaName, setSchemaName] = useState('MPA5DAT');
  const [tableName, setTableName] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sqlText, setSqlText] = useState('SELECT * FROM MPA5DAT.FATTAGEF FETCH FIRST 20 ROWS ONLY');
  const [paramsJson, setParamsJson] = useState('{}');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const [tableFilter, setTableFilter] = useState('');
  const [tableBrowserPage, setTableBrowserPage] = useState(1);
  const [tableBrowserRows, setTableBrowserRows] = useState<Record<string, any>[]>([]);
  const [tableBrowserHasMore, setTableBrowserHasMore] = useState(false);
  const [tablesCache, setTablesCache] = useState<string[]>([]);
  const [columnsByTable, setColumnsByTable] = useState<Record<string, string[]>>({});

  const tenantsQuery = useQuery({
    queryKey: ['sync-explorer', 'tenants'],
    queryFn: async () => (await syncAdminAPI.listTenants({ heartbeat_window_seconds: 180 })).data.data,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!selectedTenantId && tenantsQuery.data?.length) setSelectedTenantId(tenantsQuery.data[0].tenant_id);
  }, [selectedTenantId, tenantsQuery.data]);

  const nodesQuery = useQuery({
    queryKey: ['sync-explorer', 'nodes', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listDaemonNodes(selectedTenantId, { heartbeat_window_seconds: 180 })).data.data,
    enabled: !!selectedTenantId,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!nodeId) {
      const active = (nodesQuery.data || []).find((n: SyncDaemonNode) => n.is_active);
      if (active) setNodeId(active.node_id);
    }
  }, [nodeId, nodesQuery.data]);

  const dispatchMutation = useMutation({
    mutationFn: async (overrides?: DispatchOverrides) => {
      let parsedParams: Record<string, any> = {};
      try {
        parsedParams = paramsJson.trim() ? JSON.parse(paramsJson) : {};
      } catch {
        throw new Error('Parametri JSON non validi');
      }

      const effectiveMode = overrides?.mode ?? mode;
      const effectiveSchema = overrides?.schemaName ?? schemaName;
      const effectiveTable = overrides?.tableName ?? tableName;
      const effectivePage = overrides?.page ?? page;
      const effectivePageSize = overrides?.pageSize ?? pageSize;
      const effectiveSql = overrides?.sqlText ?? sqlText;
      const effectiveParams = overrides?.params ?? parsedParams;

      return syncAdminAPI.dispatchJob(selectedTenantId, {
        node_id: nodeId,
        command_type: effectiveMode,
        query_id: effectiveMode === 'query_preview' ? undefined : '__explorer__',
        sql_text: effectiveMode === 'query_preview' ? effectiveSql : undefined,
        schema_name: effectiveMode === 'list_tables' || effectiveMode === 'list_columns' ? effectiveSchema : undefined,
        table_name: effectiveMode === 'list_columns' ? effectiveTable : undefined,
        params: effectiveParams,
        page: effectivePage,
        page_size: effectivePageSize,
        max_pages: 1,
        priority: 0,
      });
    },
    onSuccess: (res) => {
      setActiveJobId(res.data.job_id);
    },
    onError: (error: any) => {
      toast.error(error?.message || error?.response?.data?.detail || 'Errore invio comando explorer');
    },
  });

  const activeJobQuery = useQuery({
    queryKey: ['sync-explorer', 'job', selectedTenantId, activeJobId],
    queryFn: async () => (await syncAdminAPI.getJob(selectedTenantId, activeJobId!)).data,
    enabled: !!selectedTenantId && !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data as SyncDispatchJob | undefined;
      if (!data) return 1200;
      return data.state === 'completed' || data.state === 'failed' ? false : 1200;
    },
  });

  const jobsQuery = useQuery({
    queryKey: ['sync-explorer', 'jobs', selectedTenantId],
    queryFn: async () => (await syncAdminAPI.listJobs(selectedTenantId, { limit: 30 })).data.data,
    enabled: !!selectedTenantId,
    refetchInterval: 5000,
  });

  const runExplorer = (nextPage?: number) => {
    if (!nodeId) return toast.error('Seleziona nodo daemon');
    if ((mode === 'list_tables' || mode === 'list_columns') && !schemaName.trim()) return toast.error('Schema obbligatorio');
    if (mode === 'list_columns' && !tableName.trim()) return toast.error('Tabella obbligatoria');
    if (mode === 'query_preview' && !sqlText.trim()) return toast.error('SQL obbligatoria');
    const targetPage = nextPage ?? page;
    setPage(targetPage);
    dispatchMutation.mutate({ page: targetPage });
  };

  const runTableBrowser = (nextPage: number) => {
    if (!nodeId || !schemaName.trim()) return;
    setTableBrowserPage(nextPage);
    dispatchMutation.mutate({
      mode: 'list_tables',
      page: nextPage,
      pageSize: 10,
      schemaName,
      params: tableFilter.trim() ? { table_name_like: `%${tableFilter.trim().toUpperCase()}%` } : {},
    });
  };

  const loadColumns = (schema: string, table: string) => {
    setSchemaName(schema);
    setTableName(table);
    dispatchMutation.mutate({
      mode: 'list_columns',
      schemaName: schema,
      tableName: table,
      page: 1,
      pageSize: 200,
    });
  };

  useEffect(() => {
    const progress = activeJobQuery.data?.progress as any;
    if (!progress || activeJobQuery.data?.state !== 'completed') return;

    if (progress.mode === 'list_tables' && Array.isArray(progress.records)) {
      setTableBrowserRows(progress.records);
      setTableBrowserHasMore(Boolean(progress.has_more));
      const keys = progress.records
        .map((r: Record<string, any>) => `${String(r.TABLE_SCHEMA || '').trim()}.${String(r.TABLE_NAME || '').trim()}`)
        .filter((x: string) => x !== '.');
      if (keys.length) {
        setTablesCache((prev) => Array.from(new Set([...prev, ...keys])));
      }
    }

    if (progress.mode === 'list_columns' && Array.isArray(progress.records) && progress.records.length > 0) {
      const first = progress.records[0];
      const schema = String(first.TABLE_SCHEMA || '').trim();
      const table = String(first.TABLE_NAME || '').trim();
      const cols = progress.records
        .map((r: Record<string, any>) => String(r.COLUMN_NAME || '').trim())
        .filter((c: string) => !!c);
      if (schema && table && cols.length) {
        setColumnsByTable((prev) => ({ ...prev, [`${schema}.${table}`]: cols }));
      }
    }
  }, [activeJobQuery.data]);

  useEffect(() => {
    if (!selectedTenantId || !nodeId || !schemaName.trim()) return;
    runTableBrowser(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, nodeId, schemaName]);

  useEffect(() => {
    if (!monaco) return;
    const provider = monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.', ' ', '\n'],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const textUntilCursor = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        const tableMatches = [...textUntilCursor.matchAll(/from\s+([A-Za-z0-9_#$@]+)\.([A-Za-z0-9_#$@]+)/gi)];
        const lastMatch = tableMatches.length ? tableMatches[tableMatches.length - 1] : null;
        const activeTableKey = lastMatch ? `${lastMatch[1].toUpperCase()}.${lastMatch[2].toUpperCase()}` : '';
        const cachedColumns = activeTableKey ? columnsByTable[activeTableKey] || [] : [];

        const tableSuggestions = tablesCache.map((tableFullName) => ({
          label: tableFullName,
          kind: monaco.languages.CompletionItemKind.Class,
          insertText: tableFullName,
          range,
        }));

        const columnSuggestions = cachedColumns.map((col) => ({
          label: col,
          kind: monaco.languages.CompletionItemKind.Field,
          insertText: col,
          range,
        }));

        return { suggestions: [...columnSuggestions, ...tableSuggestions] };
      },
    });
    return () => provider.dispose();
  }, [monaco, tablesCache, columnsByTable]);

  const resultColumns = (activeJobQuery.data?.progress as any)?.columns as string[] | undefined;
  const resultRows = (activeJobQuery.data?.progress as any)?.records as Record<string, any>[] | undefined;
  const hasMore = Boolean((activeJobQuery.data?.progress as any)?.has_more);

  const selectedTableColumns = useMemo(() => {
    const key = `${schemaName.toUpperCase()}.${tableName.toUpperCase()}`;
    return columnsByTable[key] || [];
  }, [columnsByTable, schemaName, tableName]);

  if (!user?.is_host) {
    return <div className="p-6 text-red-700">Accesso negato</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sync Query Explorer</h1>
        <p className="mt-1 text-sm text-gray-500">
          Browser tabelle paginato (10 righe), filtro nome, query ad-hoc con Monaco e autocomplete metadati.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm xl:grid-cols-3">
        <div className="space-y-2 xl:col-span-1">
          <h2 className="text-sm font-semibold text-gray-900">Table Browser</h2>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            {(tenantsQuery.data || []).map((t: SyncTenantAccess) => (
              <option key={t.tenant_id} value={t.tenant_id}>
                {t.tenant_name}
              </option>
            ))}
          </select>
          <select value={nodeId} onChange={(e) => setNodeId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">Nodo daemon</option>
            {(nodesQuery.data || [])
              .filter((n: SyncDaemonNode) => n.is_active)
              .map((n: SyncDaemonNode) => (
                <option key={n.node_id} value={n.node_id}>
                  {n.node_id} {n.daemon_active ? '(attivo)' : '(no hb)'}
                </option>
              ))}
          </select>
          <input
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
            placeholder="Schema (MPA5DAT)"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            placeholder="Filtro tabella (es: FATT)"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => runTableBrowser(1)}
              className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
            >
              Cerca
            </button>
            <button
              onClick={() => runTableBrowser(Math.max(1, tableBrowserPage - 1))}
              disabled={tableBrowserPage <= 1 || dispatchMutation.isPending}
              className="rounded border border-gray-300 px-3 py-2 text-xs disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={() => runTableBrowser(tableBrowserPage + 1)}
              disabled={!tableBrowserHasMore || dispatchMutation.isPending}
              className="rounded border border-gray-300 px-3 py-2 text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="rounded border border-gray-200">
            <div className="max-h-72 overflow-auto">
              {(tableBrowserRows || []).map((r, i) => {
                const schema = String(r.TABLE_SCHEMA || '').trim();
                const table = String(r.TABLE_NAME || '').trim();
                const text = String(r.TABLE_TEXT || '').trim();
                return (
                  <button
                    key={`${schema}.${table}.${i}`}
                    onClick={() => loadColumns(schema, table)}
                    className="block w-full border-b border-gray-100 px-2 py-2 text-left hover:bg-gray-50"
                  >
                    <div className="font-mono text-xs text-gray-900">{schema}.{table}</div>
                    <div className="text-[11px] text-gray-500">{text || '-'}</div>
                  </button>
                );
              })}
              {!tableBrowserRows.length && <div className="px-2 py-6 text-center text-xs text-gray-500">Nessuna tabella</div>}
            </div>
          </div>
          <div className="text-[11px] text-gray-500">Pagina browser: {tableBrowserPage} • risultati: {tableBrowserRows.length}</div>
        </div>

        <div className="space-y-3 xl:col-span-2">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <select value={mode} onChange={(e) => setMode(e.target.value as ExplorerMode)} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
              <option value="query_preview">Query preview</option>
              <option value="list_tables">Lista tabelle</option>
              <option value="list_columns">Lista colonne</option>
            </select>
            <input value={tableName} onChange={(e) => setTableName(e.target.value)} placeholder="Tabella (solo colonne)" className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
            <input type="number" min={1} value={page} onChange={(e) => setPage(Number(e.target.value))} className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
            <input type="number" min={1} max={500} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
            <button onClick={() => runExplorer()} className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">
              Esegui
            </button>
          </div>

          <div className="rounded-md border border-gray-200">
            <Editor
              height="260px"
              defaultLanguage="sql"
              value={sqlText}
              onChange={(v) => setSqlText(v || '')}
              options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on', automaticLayout: true }}
            />
          </div>

          {selectedTableColumns.length > 0 && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800">
              Colonne caricate per autocomplete ({schemaName}.{tableName}): {selectedTableColumns.join(', ')}
            </div>
          )}

          <textarea
            value={paramsJson}
            onChange={(e) => setParamsJson(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs"
            placeholder='Parametri JSON (ordine alfabetico per bind). Es: {"from_date":"2026-01-01"}'
          />
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Risultato Pagina {page}</h2>
          <div className="flex gap-2">
            <button
              onClick={() => runExplorer(Math.max(1, page - 1))}
              disabled={page <= 1 || dispatchMutation.isPending}
              className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={!hasMore || dispatchMutation.isPending}
              onClick={() => runExplorer(page + 1)}
              className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                {(resultColumns || []).map((c) => (
                  <th key={c} className="px-2 py-2 text-left font-semibold text-gray-700">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(resultRows || []).map((row, idx) => (
                <tr key={idx}>
                  {(resultColumns || []).map((c) => (
                    <td key={c} className="px-2 py-1.5 text-gray-700">
                      {String(row[c] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!resultRows?.length && <div className="py-6 text-center text-sm text-gray-500">Nessun risultato</div>}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 text-sm font-semibold text-gray-900">Storico Explorer Jobs</h2>
        <div className="space-y-2">
          {(jobsQuery.data || []).map((job: SyncDispatchJob) => (
            <button
              key={job.job_id}
              onClick={() => setActiveJobId(job.job_id)}
              className="flex w-full items-center justify-between rounded border border-gray-200 px-3 py-2 text-left hover:bg-gray-50"
            >
              <span className="font-mono text-xs text-gray-700">{job.job_id}</span>
              <span className="text-xs text-gray-600">{job.state}</span>
              <span className="text-xs text-gray-500">{formatDate(job.last_event_at || job.requested_at)}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
